import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  RefreshCw,
  Edit,
  Trash2,
  DollarSign,
  Building2,
  Calendar,
  AlertCircle,
  X,
  FileText,
  TrendingDown,
  ListChecks,
  Download,
} from 'lucide-react';
import { CompanyDeduction, Company, ViolationType } from '../types';
import apiService from '../services/api';
import { formatDateTime } from '../utils/timeUtils';

const CompanyDeductionsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [deductions, setDeductions] = useState<CompanyDeduction[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [complaints, setComplaints] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);
  const [rowPdfLoading, setRowPdfLoading] = useState<string | number | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDeduction, setEditingDeduction] = useState<CompanyDeduction | null>(null);

  const [violationTypes, setViolationTypes] = useState<ViolationType[]>([]);
  const [vtLoading, setVtLoading] = useState(false);
  const pendingViolationTypeId = React.useRef<string>('');

  const [formData, setFormData] = useState({
    companyId: '',
    complaintId: '',
    violationTypeId: '',
    notes: '',
  });

  const fetchDeductions = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiService.getCompanyDeductions();
      setDeductions(data);
      setError('');
    } catch (err) {
      setError(t('deductions.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const fetchCompanies = useCallback(async () => {
    try {
      const data = await apiService.getCompanies();
      setCompanies(data);
    } catch (err) {
      // silent
    }
  }, []);

  const fetchComplaints = useCallback(async () => {
    try {
      const data = await apiService.getComplaints(undefined, 1, 1000);
      setComplaints(data.results);
    } catch (err) {
      // silent
    }
  }, []);

  useEffect(() => {
    fetchDeductions();
    fetchCompanies();
    fetchComplaints();
  }, [fetchDeductions, fetchCompanies, fetchComplaints]);

  // When company changes in the form (user manually picks a different company), reload violation types
  useEffect(() => {
    // Skip if dialog is not open or we are in the middle of opening it (handleOpenDialog handles that case)
    if (!dialogOpen) return;
    if (!formData.companyId) {
      setViolationTypes([]);
      setFormData((prev) => ({ ...prev, violationTypeId: '' }));
      return;
    }
    // Only reset violationTypeId when the user changes the company themselves (ref is empty)
    if (pendingViolationTypeId.current) return;
    setVtLoading(true);
    apiService.getViolationTypes(formData.companyId)
      .then((data) => {
        setViolationTypes(data);
        setFormData((prev) => ({ ...prev, violationTypeId: '' }));
      })
      .catch(() => setViolationTypes([]))
      .finally(() => setVtLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.companyId]);

  const handleOpenDialog = (deduction?: CompanyDeduction) => {
    if (deduction) {
      setEditingDeduction(deduction);
      const companyId = (deduction.company?._id || deduction.company?.id)?.toString() || '';
      const violationTypeId = (deduction.violationType?._id || deduction.violationType?.id)?.toString() || '';
      // Mark ref so the companyId useEffect knows not to reset violationTypeId
      pendingViolationTypeId.current = violationTypeId;
      // Load violation types for this company directly, then set all form data
      setVtLoading(true);
      apiService.getViolationTypes(companyId)
        .then((data) => {
          setViolationTypes(data);
          setFormData({
            companyId,
            complaintId: (deduction.complaint?._id || deduction.complaint?.id)?.toString() || '',
            violationTypeId,
            notes: deduction.notes || '',
          });
        })
        .catch(() => setViolationTypes([]))
        .finally(() => {
          setVtLoading(false);
          pendingViolationTypeId.current = '';
        });
    } else {
      setEditingDeduction(null);
      setViolationTypes([]);
      pendingViolationTypeId.current = '';
      setFormData({
        companyId: '',
        complaintId: '',
        violationTypeId: '',
        notes: '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingDeduction(null);
    setViolationTypes([]);
    pendingViolationTypeId.current = '';
  };

  const handleSave = async () => {
    try {
      if (!formData.companyId || !formData.violationTypeId) {
        setError(isRTL ? 'يجب اختيار الشركة ونوع الخطية' : 'Company and violation type are required');
        return;
      }

      const payload: any = {
        company: formData.companyId,
        violationType: formData.violationTypeId,
        notes: formData.notes || undefined,
      };
      if (formData.complaintId) {
        payload.complaint = formData.complaintId;
      }

      if (editingDeduction) {
        await apiService.updateCompanyDeduction(editingDeduction.id, payload);
      } else {
        await apiService.createCompanyDeduction(payload);
      }
      await fetchDeductions();
      handleCloseDialog();
    } catch (err: any) {
      const msg = err.response?.data?.message;
      setError(Array.isArray(msg) ? msg[0] : (msg || t('deductions.saveFailed')));
    }
  };

  const handleDownloadPdf = async () => {
    try {
      setPdfLoading(true);
      const blob = await apiService.downloadDeductionsPDF();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'deductions.pdf';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      setError(isRTL ? 'فشل تحميل ملف PDF' : 'Failed to download PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const handleRowDownloadPdf = async (id: string | number) => {
    try {
      setRowPdfLoading(id);
      const blob = await apiService.downloadSingleDeductionPDF(id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `khatiya-${id}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      setError(isRTL ? 'فشل تحميل الخطية' : 'Failed to download violation PDF');
    } finally {
      setRowPdfLoading(null);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('deductions.confirmDelete'))) return;

    try {
      await apiService.deleteCompanyDeduction(id);
      await fetchDeductions();
    } catch (err) {
      setError(t('deductions.deleteFailed'));
    }
  };

  // Filtered complaints for selected company
  const filteredComplaints = formData.companyId
    ? complaints.filter((c) => {
        let cId: string | undefined;
        if (typeof c.company === 'object') {
          cId = (c.company?.id || c.company?._id)?.toString();
        } else {
          cId = c.company?.toString();
        }
        return cId === formData.companyId;
      })
    : complaints;

  // Selected violation type details for display
  const selectedVt = violationTypes.find((v) => v.id === formData.violationTypeId);

  // Calculate total deductions using violationType price
  const totalDeductions = deductions.reduce((sum, d) => sum + (d.violationType?.price ?? 0), 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-50/30 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Premium Header */}
        <div className="relative overflow-hidden rounded-3xl mb-8 shadow-2xl">
          <div className="absolute inset-0 bg-gradient-to-r from-brand-600 via-brand-700 to-emerald-700 opacity-95"></div>
          <div className="absolute inset-0 backdrop-blur-3xl bg-white/10"></div>
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -mr-48 -mt-48"></div>
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-brand-800/20 rounded-full -ml-36 -mb-36"></div>

          <div className="relative px-8 py-10">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-4xl font-bold text-white mb-2" dir={isRTL ? 'rtl' : 'ltr'}>
                  {t('deductions.title')}
                </h1>
                <p className="text-brand-50 text-lg font-medium" dir={isRTL ? 'rtl' : 'ltr'}>
                  {t('deductions.subtitle')}
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={fetchDeductions}
                  disabled={loading}
                  className="p-3 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-2xl text-white transition-all duration-300 hover:scale-105 disabled:opacity-50"
                  title={isRTL ? 'تحديث' : 'Refresh'}
                >
                  <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                </button>
                <button
                  onClick={handleDownloadPdf}
                  disabled={pdfLoading}
                  className="flex items-center gap-2 px-5 py-3 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white font-bold rounded-2xl transition-all duration-300 hover:scale-105 border border-white/30 disabled:opacity-50"
                >
                  <Download className={`w-5 h-5 ${pdfLoading ? 'animate-bounce' : ''}`} />
                  <span className="hidden sm:inline">{isRTL ? 'تحميل PDF' : 'PDF'}</span>
                </button>
                <button
                  onClick={() => handleOpenDialog()}
                  className="flex items-center gap-2 px-6 py-3 bg-white hover:bg-brand-50 text-brand-700 font-bold rounded-2xl transition-all duration-300 hover:scale-105 shadow-lg"
                >
                  <Plus className="w-5 h-5" />
                  <span>{t('deductions.new')}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 bg-red-50 border-l-4 border-red-500 rounded-2xl p-4 flex items-start gap-3 animate-fadeIn">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-red-800 font-medium">{error}</p>
            </div>
            <button onClick={() => setError('')} className="text-red-500 hover:text-red-700">
              <X className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Total Deductions Summary */}
        <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-3xl shadow-xl border border-red-100 overflow-hidden mb-8">
          <div className="p-8">
            <div className="flex items-center gap-4">
              <div className="p-4 bg-red-600 rounded-2xl">
                <TrendingDown className="w-8 h-8 text-white" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-600 mb-1" dir={isRTL ? 'rtl' : 'ltr'}>
                  {t('deductions.totalDeductions')}
                </p>
                <p className="text-3xl font-black text-gray-800">
                  {totalDeductions.toFixed(2)} {t('deductions.currency')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && deductions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-16 h-16 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin mb-4"></div>
            <p className="text-gray-500 font-medium">{t('common.loading')}</p>
          </div>
        ) : (
          <>
            {/* Deductions Table */}
            <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gradient-to-r from-brand-600 via-brand-700 to-emerald-700">
                      <th className="px-6 py-5 text-left text-sm font-bold text-white uppercase tracking-wide min-w-[180px]" dir={isRTL ? 'rtl' : 'ltr'}>
                        {t('deductions.company')}
                      </th>
                      <th className="px-6 py-5 text-left text-sm font-bold text-white uppercase tracking-wide min-w-[200px]" dir={isRTL ? 'rtl' : 'ltr'}>
                        {isRTL ? 'نوع الخطية' : 'Violation Type'}
                      </th>
                      <th className="px-6 py-5 text-left text-sm font-bold text-white uppercase tracking-wide min-w-[140px]">
                        {t('deductions.amount')}
                      </th>
                      <th className="px-6 py-5 text-left text-sm font-bold text-white uppercase tracking-wide min-w-[200px]" dir={isRTL ? 'rtl' : 'ltr'}>
                        {t('deductions.complaint')}
                      </th>
                      <th className="px-6 py-5 text-left text-sm font-bold text-white uppercase tracking-wide min-w-[180px]" dir={isRTL ? 'rtl' : 'ltr'}>
                        {isRTL ? 'ملاحظات' : 'Notes'}
                      </th>
                      <th className="px-6 py-5 text-center text-sm font-bold text-white uppercase tracking-wide min-w-[160px]">
                        {t('deductions.createdAt')}
                      </th>
                      <th className="px-6 py-5 text-center text-sm font-bold text-white uppercase tracking-wide min-w-[160px]">
                        {t('common.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {deductions.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-6 py-12 text-center">
                          <div className="flex flex-col items-center">
                            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                              <DollarSign className="w-10 h-10 text-gray-400" />
                            </div>
                            <p className="text-gray-500 font-medium">
                              {t('deductions.noDeductions')}
                            </p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      deductions.map((deduction, index) => (
                        <tr
                          key={deduction.id}
                          className="hover:bg-brand-50/30 transition-colors"
                          style={{ animationDelay: `${index * 0.05}s` }}
                        >
                          <td className="px-6 py-5 min-w-[180px]">
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-brand-600 flex-shrink-0" />
                              <span className="font-semibold text-gray-800" dir={isRTL ? 'rtl' : 'ltr'}>
                                {deduction.company?.companyName || '-'}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-5 min-w-[200px]">
                            <div className="flex items-center gap-2">
                              <ListChecks className="w-4 h-4 text-amber-600 flex-shrink-0" />
                              <span className="text-sm font-semibold text-gray-800" dir={isRTL ? 'rtl' : 'ltr'}>
                                {deduction.violationType?.name || '-'}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-5 min-w-[140px]">
                            <div className="flex items-center gap-2">
                              <div className="p-1.5 bg-red-100 rounded-lg flex-shrink-0">
                                <DollarSign className="w-4 h-4 text-red-600" />
                              </div>
                              <span className="font-bold text-red-600 whitespace-nowrap">
                                {(deduction.violationType?.price ?? 0).toFixed(2)} {t('deductions.currency')}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-5 min-w-[200px]">
                            <div className="flex items-start gap-2">
                              <FileText className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                              <span className="text-sm text-gray-700 line-clamp-2" dir={isRTL ? 'rtl' : 'ltr'}>
                                {deduction.complaint
                                  ? (deduction.complaint?.description?.substring(0, 60) || `#${deduction.complaint.id}`)
                                  : (isRTL ? 'لا يوجد' : 'None')}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-5 min-w-[180px]">
                            <span className="text-sm text-gray-500 line-clamp-2" dir={isRTL ? 'rtl' : 'ltr'}>
                              {deduction.notes || '—'}
                            </span>
                          </td>
                          <td className="px-6 py-5 min-w-[160px]">
                            <div className="flex items-center justify-center gap-2 text-gray-600">
                              <Calendar className="w-4 h-4 text-brand-600 flex-shrink-0" />
                              <span className="text-sm whitespace-nowrap">{formatDateTime(deduction.createdAt)}</span>
                            </div>
                          </td>
                          <td className="px-6 py-5 min-w-[200px]">
                            <div className="flex items-center justify-center gap-2">
                              <div className="group">
                                <button
                                  onClick={() => handleRowDownloadPdf(deduction.id)}
                                  disabled={rowPdfLoading === deduction.id}
                                  className="flex items-center gap-1.5 px-3 py-2 bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 transition-all font-semibold text-xs disabled:opacity-50"
                                >
                                  <Download className={`w-4 h-4 flex-shrink-0 ${rowPdfLoading === deduction.id ? 'animate-bounce' : ''}`} />
                                  <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-300 whitespace-nowrap">
                                    {isRTL ? 'PDF' : 'PDF'}
                                  </span>
                                </button>
                              </div>
                              <div className="group">
                                <button
                                  onClick={() => handleOpenDialog(deduction)}
                                  className="flex items-center gap-1.5 px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-all font-semibold text-xs"
                                >
                                  <Edit className="w-4 h-4 flex-shrink-0" />
                                  <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-300 whitespace-nowrap">
                                    {isRTL ? 'تعديل' : 'Edit'}
                                  </span>
                                </button>
                              </div>
                              <div className="group">
                                <button
                                  onClick={() => handleDelete(deduction.id)}
                                  className="flex items-center gap-1.5 px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-all font-semibold text-xs"
                                >
                                  <Trash2 className="w-4 h-4 flex-shrink-0" />
                                  <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-300 whitespace-nowrap">
                                    {isRTL ? 'حذف' : 'Delete'}
                                  </span>
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* Add/Edit Dialog */}
        {dialogOpen && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn">
            <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-scaleIn">
              <div className="sticky top-0 bg-gradient-to-r from-brand-600 to-brand-700 px-8 py-6 rounded-t-3xl z-10">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-white" dir={isRTL ? 'rtl' : 'ltr'}>
                    {editingDeduction ? t('deductions.editDeduction') : t('deductions.addDeduction')}
                  </h2>
                  <button onClick={handleCloseDialog} className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-all">
                    <X className="w-6 h-6 text-white" />
                  </button>
                </div>
              </div>

              <div className="p-8 space-y-6">

                {/* Company Select */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2" dir={isRTL ? 'rtl' : 'ltr'}>
                    {t('deductions.company')} <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.companyId}
                    onChange={(e) => setFormData({ ...formData, companyId: e.target.value, complaintId: '', violationTypeId: '' })}
                    required
                    dir={isRTL ? 'rtl' : 'ltr'}
                    className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:ring-4 focus:ring-brand-100 focus:bg-white transition-all outline-none font-medium"
                  >
                    <option value="">{t('deductions.selectCompany')}</option>
                    {companies.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.companyName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Violation Type Select */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2" dir={isRTL ? 'rtl' : 'ltr'}>
                    {isRTL ? 'نوع الخطية' : 'Violation Type'} <span className="text-red-500">*</span>
                  </label>
                  {!formData.companyId ? (
                    <div className="w-full px-4 py-3 bg-gray-100 border-2 border-gray-200 rounded-xl text-gray-400 text-sm font-medium" dir={isRTL ? 'rtl' : 'ltr'}>
                      {isRTL ? 'اختر الشركة أولاً' : 'Select a company first'}
                    </div>
                  ) : vtLoading ? (
                    <div className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl flex items-center gap-3">
                      <div className="w-4 h-4 border-2 border-brand-400 border-t-transparent rounded-full animate-spin"></div>
                      <span className="text-gray-500 text-sm">{isRTL ? 'جاري التحميل...' : 'Loading...'}</span>
                    </div>
                  ) : violationTypes.length === 0 ? (
                    <div className="w-full px-4 py-3 bg-amber-50 border-2 border-amber-200 rounded-xl text-amber-700 text-sm font-medium flex items-center gap-2" dir={isRTL ? 'rtl' : 'ltr'}>
                      <ListChecks className="w-4 h-4" />
                      {isRTL
                        ? 'لا توجد أنواع خطية لهذه الشركة — أضفها من صفحة إدارة الشركات'
                        : 'No violation types for this company — add them from Companies page'}
                    </div>
                  ) : (
                    <select
                      value={formData.violationTypeId}
                      onChange={(e) => setFormData({ ...formData, violationTypeId: e.target.value })}
                      required
                      dir={isRTL ? 'rtl' : 'ltr'}
                      className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:ring-4 focus:ring-brand-100 focus:bg-white transition-all outline-none font-medium"
                    >
                      <option value="">{isRTL ? 'اختر نوع الخطية' : 'Select violation type'}</option>
                      {violationTypes.map((vt) => (
                        <option key={vt.id} value={vt.id}>
                          {vt.name} — {vt.price.toFixed(2)} {isRTL ? 'ريال' : 'QAR'}
                        </option>
                      ))}
                    </select>
                  )}

                  {/* Auto-filled price preview */}
                  {selectedVt && (
                    <div className="mt-2 px-4 py-3 bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200 rounded-xl flex items-center gap-3">
                      <DollarSign className="w-5 h-5 text-amber-600 flex-shrink-0" />
                      <div dir={isRTL ? 'rtl' : 'ltr'}>
                        <p className="text-xs text-gray-500 font-medium">
                          {isRTL ? 'السعر المحدد تلقائياً' : 'Auto-filled price'}
                        </p>
                        <p className="text-lg font-black text-amber-700">
                          {selectedVt.price.toFixed(2)} {isRTL ? 'ريال' : 'QAR'}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Complaint Select (optional) */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2" dir={isRTL ? 'rtl' : 'ltr'}>
                    {t('deductions.complaint')}
                    <span className="text-xs text-gray-400 font-normal ml-2">
                      ({isRTL ? 'اختياري' : 'optional'})
                    </span>
                  </label>
                  <select
                    value={formData.complaintId}
                    onChange={(e) => setFormData({ ...formData, complaintId: e.target.value })}
                    dir={isRTL ? 'rtl' : 'ltr'}
                    className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:ring-4 focus:ring-brand-100 focus:bg-white transition-all outline-none font-medium"
                  >
                    <option value="">{isRTL ? 'بدون شكوى مرتبطة' : 'No linked complaint'}</option>
                    {filteredComplaints.map((complaint) => (
                      <option key={complaint.id} value={complaint.id}>
                        {complaint.description?.substring(0, 80) || `#${complaint.id}`}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Notes (optional) */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2" dir={isRTL ? 'rtl' : 'ltr'}>
                    {t('deductions.notes')}
                    <span className="text-xs text-gray-400 font-normal ml-2">
                      ({isRTL ? 'اختياري' : 'optional'})
                    </span>
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    dir={isRTL ? 'rtl' : 'ltr'}
                    className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:ring-4 focus:ring-brand-100 focus:bg-white transition-all outline-none font-medium resize-none"
                    placeholder={isRTL ? 'ملاحظات إضافية...' : 'Additional notes...'}
                  />
                </div>
              </div>

              <div className="sticky bottom-0 bg-gray-50 px-8 py-6 rounded-b-3xl border-t border-gray-200 flex gap-4">
                <button
                  onClick={handleCloseDialog}
                  className="flex-1 px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all font-bold"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleSave}
                  disabled={!formData.companyId || !formData.violationTypeId}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-brand-600 to-brand-700 hover:from-brand-700 hover:to-brand-800 text-white font-bold rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('common.save')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyDeductionsPage;
